import {
  Badge,
  Dropdown,
  Option,
  Text,
  tokens,
} from "@fluentui/react-components";
import useGetAllCurrencyOptions from "hooks/useGetAllCurrencyOptions";
import useGetCurrencyOptionsPerUser from "hooks/useGetCurrencyOptionsPerUser";
import { useAtom, useAtomValue } from "jotai";
import { allCurrenciesOption } from "library/constants";
import { ICompanyInfosInfo, SelectItem } from "library/types";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { selectedCompanyState } from "store/UIHrPage";
import { filterCurrencyAtom } from "store/UIHrPageDashboard";
import styled from "styled-components";

interface Props {
  companyInfo?: ICompanyInfosInfo;
  countryCode?: number;
}

const FilterCurrency = ({ companyInfo, countryCode }: Props) => {
   const { t } = useTranslation();

  const { currencyOptionsPerUser, defaultCurrencyByUserCountry } =
    useGetCurrencyOptionsPerUser({ countryCode });
  const currencyOptions = useGetAllCurrencyOptions();
  const selectedCompany = useAtomValue(selectedCompanyState);

  const allCurrencyOptions = useMemo(
    () => [...currencyOptions, allCurrenciesOption],
    [currencyOptions],
  );

  const [filterCurrency, setFilterCurrency] = useAtom(filterCurrencyAtom);

  const handleOptionSelect = (ev, data) => {
    setFilterCurrency(
      allCurrencyOptions.find(
        (e) => String(e.value) === String(data.optionValue),
      ),
    );
  };

  // Build the list: default currency (EUR), plus currencies found in packages
  const availableCurrencies = useMemo(() => {
    const retVal: SelectItem[] = [
      {
        code: "Currency.All",
        text: t("Currency.All"),
        value: "all",
      },
    ];

    // Always include EUR as the default currency
    const eurOption = currencyOptions?.find(
      (c) => c.code === "Currency.EUR",
    );
    if (eurOption) {
      retVal.push(eurOption);
    }

    // Add currencies present in the benefit implementations (packages)
    companyInfo?.benefitImplementations?.forEach((impl) => {
      const currencyToAdd =
        currencyOptionsPerUser.find(
          (c) => c.value === impl.currencyCode,
        ) ||
        allCurrencyOptions.find(
          (c) => c.value === impl.currencyCode,
        );

      if (
        currencyToAdd &&
        !retVal.some((item) => item.value === currencyToAdd.value)
      ) {
        retVal.push(currencyToAdd);
      }
    });

    return retVal;
  }, [currencyOptionsPerUser, companyInfo, allCurrencyOptions, currencyOptions]);

  // Count of distinct currencies actually present in benefit implementations
  const packageCurrencyCount = useMemo(() => {

    return availableCurrencies.filter(
      (c) => c.value !== "all" ,
    ).length;
  }, [availableCurrencies, ]);
  const hasMultipleCurrencies = packageCurrencyCount > 0;

  // When company data loads, default to "All Currencies" so summary shows
  // everything converted to the default currency
  useEffect(() => {
    if (companyInfo && !filterCurrency) {
      setFilterCurrency(allCurrenciesOption);
    }
  }, [companyInfo,selectedCompany]);

  // Reset to "All Currencies" when the selected client changes
  useEffect(() => {
      setFilterCurrency(allCurrenciesOption);
  }, [selectedCompany]);

  // The "All Currencies" entry carries no text of its own (see
  // allCurrenciesOption) — translate it here so it survives module load order
  // and language changes.
  const selectedText =
    filterCurrency?.value === allCurrenciesOption.value
      ? t("Currency.All")
      : filterCurrency?.text || "";

  return (
    <CurrencyFilterWrap>
      <Dropdown
        data-help="hr-summary-currency"
        placeholder={t("hbh.filterCurrency.placeholder")}
        title={t("hbh.filterCurrency.title")}
        style={{ marginTop: "10px" }}
        value={selectedText}
        onOptionSelect={handleOptionSelect}
      >
        {availableCurrencies.map((option) => (
          <Option key={option.code} value={String(option.value)}>
            {option.text}
          </Option>
        ))}
      </Dropdown>
      {hasMultipleCurrencies && (
        <MultipleCurrencyIndicator>
          <Badge
            appearance="filled"
            color="informative"
            size="small"
          >
            {packageCurrencyCount}
          </Badge>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {t("hbh.multipleCurrencies.label")}
          </Text>
        </MultipleCurrencyIndicator>
      )}
    </CurrencyFilterWrap>
  );
};

export default FilterCurrency;

const CurrencyFilterWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const MultipleCurrencyIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;
